import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isRoundBasedFormat } from '@draughts/shared';
import { useTournamentStore } from '../../store/tournament-store';
import { useAuthStore } from '../../store/auth-store';
import { watchTournament } from '../../services/socket';
import {
  formatDuration,
  formatTimeAgo,
  ARENA_SCORING,
  SWISS_SCORING,
} from '../../types/tournament';
import { getTimeControlName, chiaveFormatoTorneo, etichettaManche } from '../../utils/tournament-helpers';
import {
  RefreshCw,
  Swords,
  Flame,
  BarChart3,
  Trophy,
  Handshake,
  ChevronLeft,
} from 'lucide-react';
import { log } from '../../utils/logger';
import { isDisplayableRating } from '../../utils/rating';
import TournamentStandings from './TournamentStandings';
import AttesaDelTurno from './AttesaDelTurno';
import { useTablistFrecce } from '../../hooks/useTablistFrecce';
import './Tournament.css';
import './TournamentLobby.css';
import '../shared/pastiglia-formato.css';

interface TournamentLobbyProps {
  onBack: () => void;
}

type TabType = 'standings' | 'games' | 'details';

/** Le schede nell'ordine in cui stanno sullo schermo: e' l'ordine su cui si
    muovono le frecce. */
const CHIAVI_SCHEDE: TabType[] = ['standings', 'games', 'details'];

function TournamentLobby({ onBack }: TournamentLobbyProps) {
  const { t } = useTranslation();
  const {
    currentTournament,
    standings,
    status,
    error,
    tournamentGames,
    gamesFilter,
    refreshStandings,
    loadTournamentGames,
    setGamesFilter,
    currentRound,
    totalRounds,
    tournamentFormat,
    finalStandings,
  } = useTournamentStore();

  /*
   * La lobby e' la stanza di chi GIOCA, e muore col torneo.
   *
   * La fascia d'oro col vincitore stava qui, ed e' andata nella pagina di
   * dettaglio: li' la leggono anche quelli che alla fine non erano collegati,
   * cioe' quasi tutti, mentre in questa pagina ci si ritrova solo se si era
   * dentro nell'istante esatto. `App.tsx` manda al dettaglio appena il torneo
   * risulta finito, e `winner` da qui non si legge piu'.
   *
   * `torneoFinito` resta, e serve ancora: e' la manciata di istanti fra
   * l'annuncio e il cambio di pagina, in cui l'intestazione non deve dire
   * «LIVE» e la classifica dev'essere gia' quella FINALE — quella annunciata,
   * non quella che il polling potrebbe aver riscritto.
   */
  const torneoFinito = currentTournament?.status === 'finished' || status === 'finished';
  /* Due partite per accoppiamento? Lo stesso OR delle pastiglie della scheda:
     `doubleRound` e' la parola dei gironi finiti prima della migration 113,
     `twoLeg` quella di adesso. */
  const dueManche = Boolean(currentTournament?.twoLeg) || Boolean(currentTournament?.doubleRound);
  const classifica = torneoFinito && finalStandings?.length ? finalStandings : standings;

  const userId = useAuthStore((state) => state.user?.id);

  const [activeTab, setActiveTab] = useState<TabType>('standings');
  const cambiaScheda = useCallback((tab: TabType) => { setActiveTab(tab); }, []);
  const { propsScheda } = useTablistFrecce(CHIAVI_SCHEDE, activeTab, cambiaScheda);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Refresh standings on mount (e.g. after returning from a game)
  useEffect(() => {
    log.info('TournamentLobby', 'Mounted', { tournamentName: currentTournament?.name, status });
    refreshStandings();
    /*
     * E si RIENTRA nella stanza del torneo, perche' `refreshStandings` legge la
     * classifica via REST ma nella stanza non ci mette: quello lo fa solo
     * `loadTournament`.
     *
     * Questa e' la pagina dove si sta fra un turno e l'altro, e ci si arriva
     * dalla scheda del torneo — che uscendo di scena faceva `unwatch`. Il
     * risultato, visto il 18 settembre: un giocatore ha disputato tutte e tre
     * le partite (gli accoppiamenti arrivano al socket per conto loro) e poi e'
     * rimasto «in attesa» per sempre, perche' l'annuncio di fine torneo passa
     * dalla stanza da cui era appena uscito.
     *
     * Non c'e' `unwatch` all'uscita: da qui si esce per andare a GIOCARE quel
     * torneo, ed e' proprio mentre si gioca che gli annunci servono.
     */
    const id = currentTournament?.id;
    if (id) watchTournament(id);
  }, []);

  // FIX-39: Polling fallback — refresh standings every 30 s while the tournament is active
  // in case a WebSocket standings event is missed.
  useEffect(() => {
    if (currentTournament?.status !== 'active') return;

    const pollInterval = setInterval(() => {
      refreshStandings();
    }, 30_000);

    return () => clearInterval(pollInterval);
  }, [currentTournament?.status, refreshStandings]);

  // Calculate remaining time
  const calculateTimeRemaining = useCallback(() => {
    if (!currentTournament?.endsAt) return '';
    const end = new Date(currentTournament.endsAt).getTime();
    const now = Date.now();
    const diff = end - now;

    if (diff <= 0) return t('tournament.timer.finished');

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [currentTournament?.endsAt, t]);

  // Update timer every second
  useEffect(() => {
    if (currentTournament?.status !== 'active') return;

    const updateTimer = () => {
      setTimeRemaining(calculateTimeRemaining());
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [currentTournament?.status, calculateTimeRemaining]);

  // Log status changes
  useEffect(() => {
    log.info('TournamentLobby', 'Status changed', { status });
  }, [status]);

  // Load games when switching to games tab
  useEffect(() => {
    if (activeTab === 'games') {
      loadTournamentGames();
    }
  }, [activeTab, loadTournamentGames]);

  if (!currentTournament) {
    return (
      <div className="tournament-lobby">
        <button className="tournament-back-button" onClick={onBack} type="button">
          &larr; {t('common.back')}
        </button>
        <div className="tournament-lobby-error">{t('tournament.notFound')}</div>
      </div>
    );
  }

  /* A turni, non «svizzero»: il girone ha turni, classifica a punti e regole
     da mostrare esattamente come lo svizzero. */
  const aTurni = isRoundBasedFormat(tournamentFormat);
  /*
   * Il turno 1 non e' ancora stato accoppiato finche' `currentRound` vale 0: li'
   * si aspetta l'INIZIO del torneo. Da 1 in poi un turno c'e' gia' stato, quindi
   * qualunque attesa e' l'attesa del PROSSIMO — anche per chi ha appena finito
   * la sua partita mentre gli altri giocano ancora, che infatti nuovo avversario
   * non lo riceve prima del turno dopo.
   */
  /*
   * NON si distingue piu' il primo turno dagli altri.
   *
   * C'era «in attesa dell'inizio del turno» per `currentRound === 0` e «in
   * attesa del prossimo turno» da 1 in poi. Ma `currentRound` sul client puo'
   * restare indietro — chi entra nella stanza dopo l'avvio del turno non
   * riceve mai `tournament:round-start` — e la prova a mano del 19 Set l'ha
   * fatto vedere: tre finestre dicevano «prossimo turno» e una «inizio turno»,
   * nello stesso momento dello stesso torneo.
   *
   * `setRoundEnd` ora rimedia al numero, ma la distinzione non vale comunque
   * il rischio: separava una finestra di pochi secondi all'avvio del torneo, e
   * «in attesa del prossimo turno» e' vera anche li' — il primo turno E' il
   * prossimo. Una frase sola, che non puo' divergere.
   */
  /*
   * L'ULTIMO turno non ha un turno dopo, e prometterlo e' una bugia.
   *
   * Quando il turno finale si chiude il server manda `round-end` e SOLO DOPO
   * calcola la classifica finale, il rating e le scritture: in quella finestra
   * — e per tutto il turno finale, a chi ha gia' finito la sua partita — la
   * lobby restava su «in attesa del prossimo turno». Un turno che non
   * esistera' mai. Prima era una bugia ferma; unificando i due rami sarebbe
   * diventata una bugia con i puntini che rimbalzano, cioe' peggiore.
   *
   * `totalRounds > 0` e' la guardia che serve: vale 0 finche' il dettaglio non
   * e' arrivato, e senza di lei `0 >= 0` spegnerebbe l'attesa a chiunque.
   */

  // Filter games
  const filteredGames = gamesFilter === 'mine' && userId
    ? tournamentGames.filter(g => g.whiteUserId === userId || g.blackUserId === userId)
    : tournamentGames;

  /*
   * La coppia di punti della partita, sulla scala 2 / 1 / 0 (`SWISS_SCORING`).
   *
   * Prima si leggeva `1-0` / `0-1` / `=-=`: la notazione universale dell'esito,
   * che pero' qui contava due volte come dichiarazione di punteggio — il
   * dettaglio del torneo scriveva `½` per la stessa patta, e nessuna delle due
   * corrispondeva alla classifica. Ora la patta e' `1-1`, e con questo cade
   * anche `=-=`, che era l'unico posto dell'app a scrivere una patta cosi'.
   */
  const getResultBadge = (result: string | null) => {
    const { WIN, DRAW, LOSS } = SWISS_SCORING;
    if (result === 'white') return <span className="tournament-lobby-game-result white-win">{WIN}-{LOSS}</span>;
    if (result === 'black') return <span className="tournament-lobby-game-result black-win">{LOSS}-{WIN}</span>;
    if (result === 'draw') return <span className="tournament-lobby-game-result draw-result">{DRAW}-{DRAW}</span>;
    // Doppio no-show: nessuno dei due si è presentato, 0 punti a entrambi.
    if (result === 'forfeit') return <span className="tournament-lobby-game-result loss-result">{LOSS}-{LOSS}</span>;
    return <span className="tournament-lobby-game-result ongoing">...</span>;
  };

  return (
    <div className="tournament-lobby">
      {/* Header with timer */}
      <header className="tournament-lobby-header">
        <button className="tournament-back-button" onClick={onBack} type="button">
          <ChevronLeft size={20} /> <span className="tournament-back-label">{t('common.back')}</span>
        </button>
        <div className="tournament-header-center">
          <span className="tournament-eyebrow">{t('tournament.title')}</span>
          <h2 className="tournament-detail-name">{currentTournament.name}</h2>
          <div className="tournament-header-meta">
            {/* «Swiss» e «Arena» erano cablati in inglese: uscivano cosi' in
                tutte e otto le lingue, mentre le chiavi esistevano.
                Il NOME e' quello di `chiaveFormatoTorneo`: tre formati, e su un
                torneo ancora programmato il nome dice che il formato si
                decidera' all'avvio. */}
            <span className="tournament-format-badge" data-format={aTurni ? currentTournament.format ?? 'swiss' : 'arena'}>
              {aTurni ? <RefreshCw size={14} /> : <Swords size={14} />}
              {' '}
              {t(chiaveFormatoTorneo(currentTournament))}
            </span>
            <span className="tournament-tc-badge">{getTimeControlName(currentTournament.timeControl)}</span>
            {aTurni && currentRound > 0 ? (
              <span className="tournament-round-badge">{t('tournament.swiss.roundOf', { current: currentRound, total: totalRounds })}</span>
            ) : (
              <span className="tournament-duration-badge">{formatDuration(currentTournament.durationMinutes)}</span>
            )}
          </div>
        </div>
        {currentTournament.status === 'active' && timeRemaining && !torneoFinito && (
          <div className="tournament-timer-container">
            <span className="tournament-timer-label">{t('tournament.timer.remaining')}</span>
            <span className="tournament-timer-value">{timeRemaining}</span>
          </div>
        )}
      </header>

      {/* L'attesa vive in un componente suo: la rendono anche la scheda del
          torneo, e due copie della stessa logica divergono al primo
          ritocco. Le sue condizioni — torneo finito, ultimo turno, formato —
          stanno tutte li' dentro. */}
      <AttesaDelTurno />

      {error && <div className="tournament-lobby-error" role="alert">{t(error)}</div>}

      {/* Tab navigation */}
      {/* Segmentato: pattern ARIA tabs. Erano tre bottoni nudi — nessun ruolo,
          nessuno stato annunciato, nessuna freccia — ed e' lo stesso controllo
          della pagina di dettaglio, quindi la stessa tastiera. */}
      <div className="tournament-detail-tabs" role="tablist" aria-label={t('tournament.tabs.standings')}>
        {([
          { key: 'standings' as TabType, labelKey: 'tournament.tabs.standings', conteggio: null },
          { key: 'games' as TabType, labelKey: 'tournament.tabs.games', conteggio: null },
          /* Al posto di «Partecipanti», che ripeteva la classifica riga per
             riga con gli stessi nomi e gli stessi punti, «Dettagli»: quello
             che la lobby non dice da nessun'altra parte. */
          { key: 'details' as TabType, labelKey: 'tournament.tabs.details', conteggio: null },
        ]).map(({ key, labelKey, conteggio }) => (
          <button
            key={key}
            {...propsScheda(key, 'tournament-lobby')}
            data-active={activeTab === key ? 'true' : undefined}
            className={`tournament-detail-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => { cambiaScheda(key); }}
          >
            {t(labelKey)}
            {conteggio != null && <b className="tournament-tab-badge">{conteggio}</b>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="tournament-lobby-content">
        {activeTab === 'standings' && (
          <div className="tournament-lobby-standings">
            <TournamentStandings
              standings={classifica}
              aTurni={aTurni}
              girone={tournamentFormat === 'round_robin'}
              torneoFinito={torneoFinito}
              limit={20}
              showAvatar
              manche={currentTournament?.twoLeg ? 2 : 1}
              mioUserId={userId ?? null}
            />
          </div>
        )}

        {activeTab === 'games' && (
          <div className="tournament-lobby-games">
            {/* Filter bar */}
            <div className="tournament-lobby-games-filter-bar">
              <button
                className={`tournament-lobby-games-filter ${gamesFilter === 'all' ? 'active' : ''}`}
                onClick={() => setGamesFilter('all')}
                type="button"
              >
                {t('tournament.games.allGames')}
              </button>
              <button
                className={`tournament-lobby-games-filter ${gamesFilter === 'mine' ? 'active' : ''}`}
                onClick={() => setGamesFilter('mine')}
                type="button"
              >
                {t('tournament.games.myGames')}
              </button>
            </div>

            {filteredGames.length === 0 ? (
              <div className="tournament-lobby-games-empty">
                <p>{t('tournament.games.noGames')}</p>
              </div>
            ) : (
              <div className="tournament-lobby-games-list">
                {filteredGames.map((game) => (
                  <div key={game.gameId} className="tournament-lobby-game">
                    <div className="tournament-lobby-game-players">
                      <span className={`tournament-lobby-game-player ${game.result === 'white' ? 'winner' : ''}`}>
                        {game.whiteUsername}
                        {isDisplayableRating(game.whiteRating) && (
                          <span className="tournament-lobby-game-rating">({game.whiteRating})</span>
                        )}
                      </span>
                      <span className="tournament-lobby-game-vs">{t('common.vs')}</span>
                      <span className={`tournament-lobby-game-player ${game.result === 'black' ? 'winner' : ''}`}>
                        {game.blackUsername}
                        {isDisplayableRating(game.blackRating) && (
                          <span className="tournament-lobby-game-rating">({game.blackRating})</span>
                        )}
                      </span>
                    </div>
                    <div className="tournament-lobby-game-meta">
                      {/* La manche, e solo dove ce ne sono due: due righe della
                          stessa coppia nello stesso turno altrimenti non si
                          distinguono. Vedi `etichettaManche`. */}
                      {etichettaManche(t, game.leg, dueManche) && (
                        <span className="tournament-lobby-game-leg">
                          {etichettaManche(t, game.leg, dueManche)}
                        </span>
                      )}
                      {getResultBadge(game.result)}
                      <span className="tournament-lobby-game-moves">{game.movesCount} {t('tournament.games.moves')}</span>
                      <span className="tournament-lobby-game-ago">{formatTimeAgo(game.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/*
          * Le regole stavano in fondo alla pagina, in un accordeon chiuso di
          * default: sotto la classifica, sotto l'elenco partite, sotto
          * l'elenco partecipanti — cioe' dopo tutto cio' che scorre — e per
          * leggerle bisognava prima sapere che esistevano. Ora sono una
          * scheda, allo stesso livello delle altre due.
          */}
        {activeTab === 'details' && (
          <div className="tournament-lobby-details">
            {aTurni ? (
              <>
                <div className="tournament-rule-item">
                  <span className="tournament-rule-icon"><Trophy size={14} /></span>
                  <span>{t('tournament.swiss.scoringWin', { points: SWISS_SCORING.WIN })}</span>
                </div>
                <div className="tournament-rule-item">
                  <span className="tournament-rule-icon"><Handshake size={14} /></span>
                  <span>{t('tournament.swiss.scoringDraw', { points: SWISS_SCORING.DRAW })}</span>
                </div>
                <div className="tournament-rule-item">
                  <span className="tournament-rule-icon"><BarChart3 size={14} /></span>
                  {/* Il girone ha i suoi spareggi, come nella pagina del
                      torneo: senza il posto in tabella, che chiude ma non si
                      elenca. */}
                  <span>{t(tournamentFormat === 'round_robin'
                    ? 'tournament.roundRobin.tiebreakerInfo'
                    : 'tournament.swiss.tiebreakerInfo')}</span>
                </div>
              </>
            ) : (
              <>
                <div className="tournament-rule-item">
                  <span className="tournament-rule-icon"><Trophy size={14} /></span>
                  <span>{t('tournament.scoring.win', { points: ARENA_SCORING.WIN })}</span>
                </div>
                <div className="tournament-rule-item">
                  <span className="tournament-rule-icon"><Handshake size={14} /></span>
                  <span>{t('tournament.scoring.draw', { points: ARENA_SCORING.DRAW })}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/*
        * QUI NON VA UNA BARRA DI AZIONI, e non e' una dimenticanza.
        *
        * Ci stava «Abbandona torneo», ed era una promessa che il server non
        * mantiene: da `current_round > 0` in poi il ritiro e' rifiutato da
        * entrambe le porte — `swiss-handler.ts` sul socket e
        * `tournament.service.ts` sulla REST — perche' `withdrawn` non vuol dire
        * «ritirato» ma «cancellato»: la riga sparisce dalla classifica mentre i
        * suoi punti sono gia' cuciti nel Buchholz degli altri. L'unica finestra
        * in cui quel gesto funziona davvero e' prima del primo turno, e li'
        * esiste gia' col suo nome sulla scheda del torneo
        * (`TournamentDetailPage`, ramo `isScheduled`).
        *
        * Ci stavano poi «Pausa» e «Riprendi», che sono gesti dell'ARENA. In
        * un'arena l'accoppiamento e' una vasca e mettersi in pausa vuol dire
        * uscirne; in un torneo a turni gli abbinamenti li decide il turno, tutti
        * insieme, e non c'e' niente da sospendere — chi non si presenta prende
        * un no-show, che e' un'altra cosa e ha gia' il suo nome. I due bottoni
        * erano il residuo dell'epoca in cui un'arena era una riga di
        * `tournaments` (il CHECK della migration 089 ammette ancora
        * `format = 'arena'`).
        *
        * Il bisogno vero — «questo turno non posso giocarlo» — nei tornei ha
        * altri due nomi, e nessuno dei due e' la pausa: il BYE RICHIESTO
        * (meta' di un turno vinto, dichiarato prima) e il RITIRO dell'art. 5.9
        * FMJD, che lascia i risultati in classifica. Non esistono ancora;
        * quando si faranno, si faranno con quei nomi.
        */}
    </div>
  );
}

export default TournamentLobby;
